import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import PrintIcon from '@mui/icons-material/Print';
import Navbar from './Navbar';
import HeadingText from '../../BuiltInComponent/HeadingTest';
import {
  DOCUMENT_TYPES,
  createBlankPrintTemplate,
  deletePrintTemplate,
  fetchPrintTemplates,
  savePrintTemplate,
  setDefaultPrintTemplate,
  printTemplateProof,
} from '../../utils/printTemplates';

const SAMPLE_ORDER = {
  kotNo: '127',
  orderNo: '4582',
  table: 'T-12',
  orderType: 'Dine In',
  waiter: 'Ali',
  time: '09:42 PM',
  kitchen: 'Main Kitchen',
  company: 'Lucky Broast',
};

const SAMPLE_ITEMS = [
  { name: 'Chicken Broast Quarter', qty: 2, remarks: 'Less spicy' },
  { name: 'Zinger Burger', dealName: 'Zinger Deal', isDealItem: true, qty: 1, remarks: 'No mayo' },
  { name: 'Mint Margarita', qty: 3, remarks: '' },
];

const checkboxOptions = [
  ['showKotNo', 'KOT No'],
  ['showOrderNo', 'Order No'],
  ['showTable', 'Table'],
  ['showOrderType', 'Order Type'],
  ['showWaiter', 'Order Taker'],
  ['showTime', 'Time'],
];

const fontScaleSx = (section = {}) => ({
  fontSize: `${Math.max(1, Number(section.fontHeight || section.size || 1))}em`,
  transform: `scaleX(${Math.max(1, Number(section.fontWidth || section.size || 1))})`,
  transformOrigin: 'left center',
  display: 'inline-block',
  maxWidth: '100%',
});

function FontSizeControls({ title, section, onChange }) {
  const width = Number(section.fontWidth || section.size || 1);
  const height = Number(section.fontHeight || section.size || 1);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>{title} Font</Typography>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Typography variant="caption">Width {width}x</Typography>
          <Slider value={width} min={1} max={8} step={1} marks onChange={(_event, value) => onChange({ fontWidth: value })} />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Typography variant="caption">Height {height}x</Typography>
          <Slider value={height} min={1} max={8} step={1} marks onChange={(_event, value) => onChange({ fontHeight: value })} />
        </Grid2>
      </Grid2>
    </Box>
  );
}

function LineCharControls({ title, section, paperMm, onChange }) {
  const fallback = paperMm === 58 ? 32 : 48;
  const value = Number(section.charsPerLine || 0);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>{title} Line Chars</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          label="Chars"
          type="number"
          size="small"
          value={value || ''}
          placeholder={String(fallback)}
          onChange={(event) => onChange({ charsPerLine: Number(event.target.value || 0) })}
          inputProps={{ min: 0, max: 80 }}
          sx={{ maxWidth: 140 }}
        />
        <Button size="small" variant="outlined" onClick={() => onChange({ charsPerLine: 0 })}>
          Auto
        </Button>
      </Stack>
    </Box>
  );
}

function FieldRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <span>{label}</span>
      <strong>{value}</strong>
    </Box>
  );
}

function ThermalPreview({ template }) {
  const design = template.designJson || {};
  const header = design.header || {};
  const info = design.info || {};
  const items = design.items || {};
  const footer = design.footer || {};
  const previewWidth = template.paperMm === 58 ? 260 : 340;
  const charWidth = (section) => Number(section?.charsPerLine || 0) || (template.paperMm === 58 ? 32 : 48);
  const clampText = (value, width) => String(value || '').slice(0, Math.max(1, Number(width || 1)));

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: previewWidth,
          minHeight: 520,
          bgcolor: '#fff',
          color: '#111',
          border: '1px solid #d7d7d7',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          p: 2,
          fontFamily: 'Consolas, "Courier New", monospace',
          fontSize: template.paperMm === 58 ? 12 : 13,
          lineHeight: 1.45,
        }}
      >
        <Box
          sx={{
            textAlign: header.align || 'center',
            fontWeight: header.bold ? 800 : 500,
            fontSize: header.size === 3 ? 24 : header.size === 2 ? 20 : 16,
            textTransform: 'uppercase',
          }}
        >
          <Box component="span" sx={fontScaleSx(header)}>{clampText(header.title || template.documentType, charWidth(header))}</Box>
        </Box>
        {header.showCompanyName && (
          <Box sx={{ textAlign: header.align || 'center' }}><Box component="span" sx={fontScaleSx(header)}>{clampText(SAMPLE_ORDER.company, charWidth(header))}</Box></Box>
        )}
        {header.showKitchenName && (
          <Box sx={{ textAlign: header.align || 'center' }}><Box component="span" sx={fontScaleSx(header)}>{clampText(SAMPLE_ORDER.kitchen, charWidth(header))}</Box></Box>
        )}
        <Divider sx={{ my: 1, borderColor: '#111' }} />

        <Box sx={{ ...fontScaleSx(info), maxWidth: `${charWidth(info)}ch` }}>
          {info.showKotNo && <FieldRow label="KOT #:" value={SAMPLE_ORDER.kotNo} />}
          {info.showOrderNo && <FieldRow label="Order #:" value={SAMPLE_ORDER.orderNo} />}
          {info.showTable && <FieldRow label="Table:" value={SAMPLE_ORDER.table} />}
          {info.showOrderType && <FieldRow label="Type:" value={SAMPLE_ORDER.orderType} />}
          {info.showWaiter && <FieldRow label="Order Taker:" value={SAMPLE_ORDER.waiter} />}
          {info.showTime && <FieldRow label="Time:" value={SAMPLE_ORDER.time} />}
        </Box>

        <Divider sx={{ my: 1, borderColor: '#111' }} />
        <Box sx={{ ...fontScaleSx(items), maxWidth: `${charWidth(items)}ch` }}>
        {items.showItemSectionHeading !== false && (
          <Box sx={{ fontWeight: 800, mb: 1 }}>*** NEW ITEMS ***</Box>
        )}
        {SAMPLE_ITEMS.map((item, index) => (
          <Box key={item.name} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ fontWeight: items.itemBold ? 800 : 500 }}>
                {items.showSerial ? `${index + 1}. ` : ''}{item.isDealItem && items.showDealItemNames === false ? item.dealName : item.isDealItem ? `${item.name} (Deal: ${item.dealName})` : item.name}
              </Box>
              {items.showQty && <Box>{items.qtyLabel || 'Qty'} {items.separator || ':'} {item.qty}</Box>}
            </Box>
            {items.showRemarks && item.remarks && (
              <Box sx={{ pl: 1, color: '#555' }}>Note: {item.remarks}</Box>
            )}
          </Box>
        ))}
        </Box>

        <Divider sx={{ my: 1, borderColor: '#111' }} />
        {footer.show && (
          <Box sx={{ textAlign: 'center', fontWeight: 700 }}><Box component="span" sx={fontScaleSx(footer)}>{clampText(footer.text, charWidth(footer))}</Box></Box>
        )}
        {footer.cutPaper && <Box sx={{ mt: 2, textAlign: 'center' }}>-- cut --</Box>}
      </Box>
    </Box>
  );
}

export default function PrintTemplateDesigner() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(createBlankPrintTemplate());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proofing, setProofing] = useState(false);
  const [printerIP, setPrinterIP] = useState('192.168.100.77');
  const [printerPort, setPrinterPort] = useState(9100);
  const [message, setMessage] = useState('');

  const selectedID = selected?.templateID || 0;

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await fetchPrintTemplates();
        setTemplates(data);
        if (data.length) {
          setSelected(data.find((item) => item.isDefault) || data[0]);
        }
      } catch (error) {
        setMessage('Print templates load nahi ho sakin.');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const groupedTemplates = useMemo(() => (
    DOCUMENT_TYPES.map((doc) => ({
      ...doc,
      templates: templates.filter((template) => template.documentType === doc.value),
    }))
  ), [templates]);

  const updateTemplate = (patch) => {
    setSelected((prev) => ({ ...prev, ...patch }));
  };

  const updateDesign = (section, patch) => {
    setSelected((prev) => ({
      ...prev,
      designJson: {
        ...prev.designJson,
        [section]: {
          ...(prev.designJson?.[section] || {}),
          ...patch,
        },
      },
    }));
  };

  const handleNew = () => {
    setSelected(createBlankPrintTemplate());
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await savePrintTemplate(selected);
      setSelected(saved);
      setTemplates((prev) => {
        const exists = prev.some((item) => item.templateID === saved.templateID);
        const next = exists
          ? prev.map((item) => (item.templateID === saved.templateID ? saved : item))
          : [...prev, saved];
        return saved.isDefault
          ? next.map((item) => item.documentType === saved.documentType && item.templateID !== saved.templateID ? { ...item, isDefault: false } : item)
          : next;
      });
      setMessage('Print template save ho gaya.');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Print template save nahi ho saka.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedID) {
      handleNew();
      return;
    }

    setSaving(true);
    try {
      await deletePrintTemplate(selectedID);
      const remaining = templates.filter((item) => item.templateID !== selectedID);
      setTemplates(remaining);
      setSelected(remaining[0] || createBlankPrintTemplate());
      setMessage('Print template delete ho gaya.');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Print template delete nahi ho saka.');
    } finally {
      setSaving(false);
    }
  };

  const handleProofPrint = async () => {
    if (!String(printerIP || '').trim()) {
      setMessage('Printer IP required hai.');
      return;
    }

    setProofing(true);
    try {
      await printTemplateProof({
        template: selected,
        printerIP: String(printerIP).trim(),
        printerPort: Number(printerPort || 9100),
      });
      setMessage('Proof print printer ko send ho gaya.');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Proof print nahi ho saka.');
    } finally {
      setProofing(false);
    }
  };

  const handleDefault = async () => {
    if (!selectedID) {
      updateTemplate({ isDefault: true });
      return;
    }

    setSaving(true);
    try {
      await setDefaultPrintTemplate(selectedID);
      setTemplates((prev) => prev.map((item) => (
        item.documentType === selected.documentType
          ? { ...item, isDefault: item.templateID === selectedID }
          : item
      )));
      setSelected((prev) => ({ ...prev, isDefault: true }));
      setMessage('Default template update ho gaya.');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Default update nahi ho saka.');
    } finally {
      setSaving(false);
    }
  };

  const header = selected.designJson?.header || {};
  const info = selected.designJson?.info || {};
  const items = selected.designJson?.items || {};
  const footer = selected.designJson?.footer || {};

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} alignItems={{ sm: 'center' }}>
          <HeadingText text="Print Template Designer" />
          <Stack direction="row" spacing={1}>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={handleNew} disabled={saving}>
              New
            </Button>
            <Button startIcon={<StarIcon />} variant="outlined" onClick={handleDefault} disabled={saving}>
              Default
            </Button>
            <Button startIcon={<DeleteIcon />} color="error" variant="outlined" onClick={handleDelete} disabled={saving}>
              Delete
            </Button>
            <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>

        <Grid2 container spacing={2} sx={{ mt: 2 }}>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Saved Designs</Typography>
                {loading && <Alert severity="info">Loading templates...</Alert>}
                <List dense>
                  {groupedTemplates.map((group) => (
                    <Box key={group.value}>
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1, textTransform: 'uppercase' }}>
                        {group.label}
                      </Typography>
                      {group.templates.map((template) => (
                        <ListItemButton
                          key={template.templateID}
                          selected={template.templateID === selectedID}
                          onClick={() => setSelected(template)}
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={`${template.templateName}${template.isDefault ? ' *' : ''}`}
                            secondary={`${template.paperMm}mm`}
                          />
                        </ListItemButton>
                      ))}
                    </Box>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 5 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 7 }}>
                      <TextField
                        label="Template Name"
                        value={selected.templateName || ''}
                        fullWidth
                        onChange={(event) => updateTemplate({ templateName: event.target.value })}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 5 }}>
                      <FormControl fullWidth>
                        <InputLabel>Document Type</InputLabel>
                        <Select
                          label="Document Type"
                          value={selected.documentType}
                          onChange={(event) => updateTemplate({ documentType: event.target.value })}
                        >
                          {DOCUMENT_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>
                  </Grid2>

                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={selected.paperMm}
                      onChange={(_event, value) => value && updateTemplate({ paperMm: value })}
                    >
                      <ToggleButton value={58}>58mm</ToggleButton>
                      <ToggleButton value={80}>80mm</ToggleButton>
                    </ToggleButtonGroup>
                    <FormControlLabel
                      control={<Switch checked={Boolean(selected.isDefault)} onChange={(_event, checked) => updateTemplate({ isDefault: checked })} />}
                      label="Default"
                    />
                  </Stack>

                  <Divider />

                  <Typography variant="h6">Header</Typography>
                  <TextField
                    label="Title"
                    value={header.title || ''}
                    onChange={(event) => updateDesign('header', { title: event.target.value })}
                    fullWidth
                  />
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={header.align || 'center'}
                    onChange={(_event, value) => value && updateDesign('header', { align: value })}
                  >
                    <ToggleButton value="left">Left</ToggleButton>
                    <ToggleButton value="center">Center</ToggleButton>
                    <ToggleButton value="right">Right</ToggleButton>
                  </ToggleButtonGroup>
                  <FontSizeControls title="Header" section={header} onChange={(patch) => updateDesign('header', patch)} />
                  <LineCharControls title="Header" section={header} paperMm={selected.paperMm} onChange={(patch) => updateDesign('header', patch)} />
                  <Stack direction="row" flexWrap="wrap">
                    <FormControlLabel control={<Checkbox checked={Boolean(header.bold)} onChange={(_event, checked) => updateDesign('header', { bold: checked })} />} label="Bold" />
                    <FormControlLabel control={<Checkbox checked={Boolean(header.showCompanyName)} onChange={(_event, checked) => updateDesign('header', { showCompanyName: checked })} />} label="Company" />
                    <FormControlLabel control={<Checkbox checked={Boolean(header.showKitchenName)} onChange={(_event, checked) => updateDesign('header', { showKitchenName: checked })} />} label="Kitchen" />
                  </Stack>

                  <Divider />

                  <Typography variant="h6">Info Fields</Typography>
                  <FontSizeControls title="Info" section={info} onChange={(patch) => updateDesign('info', patch)} />
                  <LineCharControls title="Info" section={info} paperMm={selected.paperMm} onChange={(patch) => updateDesign('info', patch)} />
                  <Stack direction="row" flexWrap="wrap">
                    {checkboxOptions.map(([key, label]) => (
                      <FormControlLabel
                        key={key}
                        control={<Checkbox checked={Boolean(info[key])} onChange={(_event, checked) => updateDesign('info', { [key]: checked })} />}
                        label={label}
                      />
                    ))}
                  </Stack>

                  <Divider />

                  <Typography variant="h6">Items</Typography>
                  <FontSizeControls title="Items" section={items} onChange={(patch) => updateDesign('items', patch)} />
                  <LineCharControls title="Items" section={items} paperMm={selected.paperMm} onChange={(patch) => updateDesign('items', patch)} />
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Qty Label"
                        value={items.qtyLabel || ''}
                        onChange={(event) => updateDesign('items', { qtyLabel: event.target.value })}
                        fullWidth
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Separator"
                        value={items.separator || ''}
                        onChange={(event) => updateDesign('items', { separator: event.target.value })}
                        fullWidth
                      />
                    </Grid2>
                  </Grid2>
                  <Stack direction="row" flexWrap="wrap">
                    <FormControlLabel control={<Checkbox checked={Boolean(items.showSerial)} onChange={(_event, checked) => updateDesign('items', { showSerial: checked })} />} label="Serial" />
                    <FormControlLabel control={<Checkbox checked={Boolean(items.showQty)} onChange={(_event, checked) => updateDesign('items', { showQty: checked })} />} label="Qty" />
                    <FormControlLabel control={<Checkbox checked={items.showItemSectionHeading !== false} onChange={(_event, checked) => updateDesign('items', { showItemSectionHeading: checked })} />} label="Item Heading" />
                    <FormControlLabel control={<Checkbox checked={Boolean(items.showRemarks)} onChange={(_event, checked) => updateDesign('items', { showRemarks: checked })} />} label="Remarks" />
                    <FormControlLabel control={<Checkbox checked={items.showDealItemNames !== false} onChange={(_event, checked) => updateDesign('items', { showDealItemNames: checked })} />} label="Deal Item Names" />
                    <FormControlLabel control={<Checkbox checked={Boolean(items.itemBold)} onChange={(_event, checked) => updateDesign('items', { itemBold: checked })} />} label="Item Bold" />
                  </Stack>

                  <Divider />

                  <Typography variant="h6">Footer</Typography>
                  <FontSizeControls title="Footer" section={footer} onChange={(patch) => updateDesign('footer', patch)} />
                  <LineCharControls title="Footer" section={footer} paperMm={selected.paperMm} onChange={(patch) => updateDesign('footer', patch)} />
                  <TextField
                    label="Footer Text"
                    value={footer.text || ''}
                    onChange={(event) => updateDesign('footer', { text: event.target.value })}
                    fullWidth
                  />
                  <Stack direction="row" flexWrap="wrap">
                    <FormControlLabel control={<Checkbox checked={Boolean(footer.show)} onChange={(_event, checked) => updateDesign('footer', { show: checked })} />} label="Show Footer" />
                    <FormControlLabel control={<Checkbox checked={Boolean(footer.cutPaper)} onChange={(_event, checked) => updateDesign('footer', { cutPaper: checked })} />} label="Cut Paper" />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Proof Printer</Typography>
                  <Stack spacing={2}>
                    <Grid2 container spacing={1}>
                      <Grid2 size={{ xs: 12, sm: 8 }}>
                        <TextField
                          label="Printer IP"
                          value={printerIP}
                          onChange={(event) => setPrinterIP(event.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="Port"
                          type="number"
                          value={printerPort}
                          onChange={(event) => setPrinterPort(event.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid2>
                    </Grid2>
                    <Button
                      startIcon={<PrintIcon />}
                      variant="contained"
                      color="secondary"
                      onClick={handleProofPrint}
                      disabled={proofing || saving}
                      fullWidth
                    >
                      {proofing ? 'Printing...' : 'Print Proof'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Preview</Typography>
                  <ThermalPreview template={selected} />
                </CardContent>
              </Card>
            </Stack>
          </Grid2>
        </Grid2>
      </Container>

      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage('')}>
        <Alert onClose={() => setMessage('')} severity="info" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
}

